import {AfterViewInit, Component, ElementRef, Input, OnDestroy, QueryList, ViewChildren} from '@angular/core';

@Component({
    selector: 'app-video-full-width',
    templateUrl: './video-full-width.component.html',
    styleUrls: ['./video-full-width.component.scss']
})
export class VideoFullWidthComponent implements AfterViewInit, OnDestroy {
    @Input() sectionId = '';
    @Input() title = '';
    @Input() titleHtml = '';
    @Input() videoSrcDesktop = '';
    @Input() videoSrcMobile = '';
    @Input() posterDesktop = '/assets/images/video-placeholder-desktop.jpg';
    @Input() posterMobile = '';
    @Input() ariaLabel: string | null = null;
    @Input() withOverlayBackground = false;

    @ViewChildren('autoVideo') autoVideos!: QueryList<ElementRef<HTMLVideoElement>>;

    private sectionObserver?: IntersectionObserver;
    private sectionInView = false;
    private hasAutoplayRun = false;
    private videoPlayHandlerMap = new Map<HTMLVideoElement, () => void>();

    constructor(private hostRef: ElementRef<HTMLElement>) {
    }

    get mobileSrc(): string {
        return this.videoSrcMobile || this.videoSrcDesktop;
    }

    get mobilePoster(): string {
        return this.posterMobile || this.posterDesktop;
    }

    ngAfterViewInit() {
        this.sectionObserver = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                this.sectionInView =
                    entry.isIntersecting && entry.intersectionRatio >= 0.35;

                this.syncPlaybackState();
            },
            {threshold: [0.35, 0.6]}
        );

        this.sectionObserver.observe(this.hostRef.nativeElement);

        // Attach play event listeners to coordinate pausing across all video-full-width instances
        this.autoVideos.forEach((videoRef) => {
            const video = videoRef.nativeElement;
            const handler = () => this.pauseOtherFullWidthVideos(video);
            video.addEventListener('play', handler);
            this.videoPlayHandlerMap.set(video, handler);
        });
    }

    ngOnDestroy() {
        // Remove play event listeners
        this.autoVideos?.forEach((videoRef) => {
            const video = videoRef.nativeElement;
            const handler = this.videoPlayHandlerMap.get(video);
            if (handler) {
                video.removeEventListener('play', handler);
            }
        });
        this.videoPlayHandlerMap.clear();

        this.sectionObserver?.disconnect();
    }

    private syncPlaybackState() {
        if (!this.autoVideos?.length) {
            return;
        }

        if (!this.sectionInView) {
            this.pauseAllVideos();
            return;
        }

        this.autoVideos.forEach((videoRef) => {
            const video = videoRef.nativeElement;
            if (!video.querySelector('source')?.getAttribute('src')) {
                return;
            }
            const style = getComputedStyle(video);
            const isVisible =
                style.display !== 'none' && style.visibility !== 'hidden';

            if (isVisible && !this.hasAutoplayRun) {
                this.pauseOtherFullWidthVideos(video);
                video.muted = true;
                video.setAttribute('playsinline', 'true');
                void video.play().catch(() => {
                });
                this.hasAutoplayRun = true;
            }
        });
    }

    private pauseAllVideos() {
        if (!this.autoVideos?.length) {
            return;
        }
        this.autoVideos.forEach((videoRef) => {
            videoRef.nativeElement.pause();
        });
    }

    private pauseOtherFullWidthVideos(activeVideo: HTMLVideoElement) {
        const allFullWidthVideos = document.querySelectorAll('.video-full-width-section video');
        allFullWidthVideos.forEach((videoEl) => {
            const video = videoEl as HTMLVideoElement;
            if (video !== activeVideo) {
                video.pause();
            }
        });
    }
}
